import React, { useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import AnimatedCounter from "../../components/AnimatedCounter";
import { FaUsers, FaGraduationCap, FaHeartbeat, FaRoad, FaCheckCircle, FaArrowRight } from "react-icons/fa";
import classStudents from "../../assets/gallery/education/classstudents.png";
import techTraining1 from "../../assets/gallery/education/tech-training1.jpg";
import techTraining2 from "../../assets/gallery/education/tech-training2.jpg";
import groupEventAsset from "../../assets/gallery/events/groupix.png";
import healthOutreach1 from "../../assets/gallery/health/health-outreach1.jpeg";
import roadConstruction1 from "../../assets/gallery/community-projects/road-construction1.jpeg";
import scholarship from "../../assets/gallery/education/scholarship.jpeg";
import health from "../../assets/gallery/health/health.jpeg";
import { getCollageImages } from "../../utils/heroImages";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function About() {
  const groupEvent = `${groupEventAsset}?v=20260424`;
  return (
    <div className="w-full bg-white">
      <HeroSection
        title="About Ojoto Union"
        subtitle="The official umbrella organization for Ojotorians residing in the United States and Canada, fostering unity, development, and cultural preservation."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        collageImages={getCollageImages("aboutOverview")}
      />

      {/* Intro Section */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 700,
                  color: '#1F2937'
                }}
              >
                Who We Are
              </h2>
              <div 
                className="mx-auto mb-6"
                style={{
                  width: '80px',
                  height: '3px',
                  backgroundColor: '#F59E0B'
                }}
              ></div>
              <p 
                className="text-lg leading-relaxed mb-8"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 400,
                  color: '#374151',
                  lineHeight: 1.8
                }}
              >
                Ojoto Union North America is the official overseas branch of OJOTO AKANASATO UNION - 
                    the recognized town leadership association for the people of Ojoto, in Idemili South Local Government Area, 
                Anambra State, Nigeria. Established in 2010, we've been serving our community with dedication and purpose.
                  </p>
              </div>
            </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <AnimatedSection delay={0.2}>
              <div 
                className="card"
                style={{
                  padding: '2rem',
                  minHeight: '400px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <h3 
                  className="text-2xl font-bold mb-4"
                  style={{
                    fontFamily: "'Open Sans', system-ui, sans-serif",
                    fontWeight: 700,
                    color: '#1F2937'
                  }}
                >
                    Our Mission
                  </h3>
                <p 
                  className="mb-6 flex-grow"
                  style={{
                    fontFamily: "'Open Sans', system-ui, sans-serif",
                    fontWeight: 400,
                    color: '#374151',
                    lineHeight: 1.7,
                    fontSize: '16px'
                  }}
                >
                  To foster unity, development, and cultural preservation among Ojotorians in North America 
                    while maintaining strong ties with our homeland for sustainable progress.
                  </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="text-[#1E40AF] text-xl flex-shrink-0 mt-1" />
                    <span 
                      className="text-gray-700"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif",
                        fontWeight: 400,
                        lineHeight: 1.6
                      }}
                    >
                      Cultural preservation and heritage celebration
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="text-[#1E40AF] text-xl flex-shrink-0 mt-1" />
                    <span 
                      className="text-gray-700"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif",
                        fontWeight: 400,
                        lineHeight: 1.6
                      }}
                    >
                      Educational scholarships and professional development
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="text-[#1E40AF] text-xl flex-shrink-0 mt-1" />
                    <span 
                      className="text-gray-700"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif",
                        fontWeight: 400,
                        lineHeight: 1.6
                      }}
                    >
                      Community development projects in our hometown
                    </span>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <div className="relative">
                <img
                  src={scholarship}
                  alt="Ojoto Union Community"
                  className="w-full h-auto rounded-lg card-image"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    maxHeight: '500px',
                    objectFit: 'cover'
                  }}
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 700,
                  color: '#1F2937'
                }}
              >
                What We Do
              </h2>
              <div 
                className="mx-auto mb-4"
                style={{
                  width: '80px',
                  height: '3px',
                  backgroundColor: '#F59E0B'
                }}
              ></div>
              <p 
                className="text-lg max-w-2xl mx-auto"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 400,
                  color: '#374151',
                  lineHeight: 1.7
                }}
              >
                We provide comprehensive support to Ojotorians in North America through various programs and initiatives.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {[
              {
                icon: <FaGraduationCap className="text-3xl text-primary-600" />,
                title: "Scholarship Programs",
                description: "Providing financial aid and educational support to deserving Ojotorian students in North America and our homeland."
              },
              {
                icon: <FaUsers className="text-3xl text-primary-600" />,
                title: "Community Networking",
                description: "Connecting Ojotorians across North America to foster professional growth, collaboration, and mutual support."
              },
              {
                icon: <FaHeartbeat className="text-3xl text-primary-600" />,
                title: "Health Outreach",
                description: "Organizing health awareness campaigns, medical missions, and support for healthcare initiatives in Ojoto."
              },
              {
                icon: <FaRoad className="text-3xl text-primary-600" />,
                title: "Community Development",
                description: "Initiating and supporting infrastructure projects, economic empowerment, and sustainable development in Ojoto."
              }
            ].map((item, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="card text-center"
                  style={{
                    minHeight: '240px'
                  }}
                >
                  <div className="flex justify-center mb-6">
                    {item.icon}
                  </div>
                  <h3 
                    className="text-xl font-bold mb-3"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1F2937',
                      lineHeight: 1.3
                    }}
                  >
                    {item.title}
                  </h3>
                  <p 
                    className="text-gray-600 flex-grow"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      color: '#374151',
                      lineHeight: 1.6
                    }}
                  >
                    {item.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section bg-white">
        <div className="section-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {[
              { value: 500, suffix: "+", label: "Active Members" },
              { value: 15, suffix: "+", label: "Years Serving" },
              { value: 2, prefix: "$", suffix: "M+", label: "Projects Funded" },
              { value: 50, suffix: "+", label: "Scholarships Awarded" }
            ].map((stat, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div 
                  className="card text-center"
                  style={{
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <div 
                    className="text-4xl md:text-5xl font-bold mb-3"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1E40AF',
                      lineHeight: 1.2
                    }}
                  >
                    <AnimatedCounter 
                      end={stat.value} 
                      prefix={stat.prefix || ""} 
                      suffix={stat.suffix || ""} 
                      duration={2000} 
                      />
                    </div>
                  <div 
                    className="text-gray-600"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      color: '#374151'
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Ready to Join Our Community?
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              Become part of a vibrant community of Ojotorians working together to preserve our culture and support our hometown.
            </p>
                    <Link
                      to="/register"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Apply Now
              <FaArrowRight />
                    </Link>
            </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
